/**
 * Utility functions for JWT token management
 */

export interface TokenInfo {
  isValid: boolean;
  isExpired: boolean;
  expiresAt?: Date;
  timeUntilExpiry?: number; // seconds
  subject?: string;
  issuer?: string;
}

/**
 * Decode and analyze a JWT token
 */
export const analyzeToken = (token: string): TokenInfo => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return { isValid: false, isExpired: true };
    }

    const payload = JSON.parse(atob(parts[1]));
    const now = Math.floor(Date.now() / 1000);
    const expiresAt = payload.exp ? new Date(payload.exp * 1000) : undefined;
    const isExpired = payload.exp ? payload.exp < now : true;
    const timeUntilExpiry = payload.exp ? payload.exp - now : 0;

    return {
      isValid: true,
      isExpired,
      expiresAt,
      timeUntilExpiry,
      subject: payload.sub,
      issuer: payload.iss
    };
  } catch (error) {
    console.error('Error analyzing token:', error);
    return { isValid: false, isExpired: true };
  }
};

/**
 * Check if a token needs renewal (expires within threshold)
 */
export const needsRenewal = (token: string, thresholdSeconds: number = 300): boolean => {
  const info = analyzeToken(token);
  return !info.isValid || info.isExpired || (info.timeUntilExpiry !== undefined && info.timeUntilExpiry < thresholdSeconds);
};

/**
 * Clear all authentication-related storage
 */
export const clearAuthStorage = (): void => {
  // Clear OIDC storage
  const keysToRemove = [
    'oidc.user',
    'oidc.user.default',
    'oidc.settings',
    'oidc.settings.default'
  ];

  keysToRemove.forEach(key => {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  });

  // Clear any other auth-related items
  sessionStorage.removeItem('lastSignInAttempt');
  sessionStorage.removeItem('intendedPath');
  
  console.debug('🧹 Cleared authentication storage');
};

/**
 * Log token information for debugging (without exposing the actual token)
 */
export const logTokenInfo = (token: string, context: string = ''): void => {
  const info = analyzeToken(token);
  
  console.debug(`🔍 Token info ${context}:`, {
    isValid: info.isValid,
    isExpired: info.isExpired,
    expiresAt: info.expiresAt?.toISOString(),
    timeUntilExpiry: info.timeUntilExpiry ? `${info.timeUntilExpiry}s` : 'unknown',
    subject: info.subject,
    issuer: info.issuer
  });
};